import { createSelector } from "@reduxjs/toolkit";
import {
  AggregatedSubjectCompletion,
  AggregatedSubjectPerformance,
} from "@tsTypes/user-metadata-schema";
import type { TypedUseSelectorHook } from "react-redux";
import { useDispatch, useSelector } from "react-redux";
import type { AppDispatch, RootState } from "../../app/store";
import { getSubjectCompletionData, getSubjectPerformanceData } from "./helpers";

export const useCustomDispatch: () => AppDispatch = useDispatch;
export const useCustomSelector: TypedUseSelectorHook<RootState> = useSelector;

export function useResourceCompletionSelector() {
  return useCustomSelector(
    createSelector(
      [
        (state: RootState) => state.SubtopicUserReduxMetadata,
        (state: RootState) => state.TopicTestUserReduxMetadata,
        (state: RootState) => state.CustomTestUserReduxMetadata,
        (state: RootState) => state.MockExamUserReduxMetadata,
      ],
      (
        SubtopicUserReduxMetadata,
        TopicTestUserReduxMetadata,
        CustomTestUserReduxMetadata,
        MockExamUserReduxMetadata
      ) => {
        return Object.entries(
          SubtopicUserReduxMetadata
        ).reduce<AggregatedSubjectCompletion>(
          (acc, [subjectDocumentId, subjectData]) => {
            acc[subjectDocumentId] = getSubjectCompletionData(
              subjectDocumentId,
              SubtopicUserReduxMetadata,
              TopicTestUserReduxMetadata,
              CustomTestUserReduxMetadata,
              MockExamUserReduxMetadata
            );
            return acc;
          },
          {}
        );
      }
    )
  );
}

export function useResourcePerformanceSelector() {
  return useCustomSelector(
    createSelector(
      [
        (state: RootState) => state.SubtopicUserReduxMetadata,
        (state: RootState) => state.TopicTestUserReduxMetadata,
        (state: RootState) => state.CustomTestUserReduxMetadata,
        (state: RootState) => state.MockExamUserReduxMetadata,
      ],
      (
        SubtopicUserReduxMetadata,
        TopicTestUserReduxMetadata,
        CustomTestUserReduxMetadata,
        MockExamUserReduxMetadata
      ) => {
        return Object.entries(
          SubtopicUserReduxMetadata
        ).reduce<AggregatedSubjectPerformance>(
          (acc, [subjectDocumentId, subjectData]) => {
            acc[subjectDocumentId] = getSubjectPerformanceData(
              subjectDocumentId,
              SubtopicUserReduxMetadata,
              TopicTestUserReduxMetadata,
              CustomTestUserReduxMetadata,
              MockExamUserReduxMetadata
            );
            return acc;
          },
          {}
        );
      }
    )
  );
}

export function useUserReduxProgressMetadataSelector() {
  return useCustomSelector(
    createSelector(
      [
        (state: RootState) => state.SubtopicUserReduxMetadata,
        (state: RootState) => state.TopicTestUserReduxMetadata,
        (state: RootState) => state.CustomTestUserReduxMetadata,
        (state: RootState) => state.subjectSyllabus,
        (state: RootState) => state.MockExamUserReduxMetadata,
      ],
      (
        SubtopicUserReduxMetadata,
        TopicTestUserReduxMetadata,
        CustomTestUserReduxMetadata,
        SubjectSyllabus,
        MockExamUserReduxMetadata
      ) => ({
        SubtopicUserReduxMetadata,
        TopicTestUserReduxMetadata,
        CustomTestUserReduxMetadata,
        SubjectSyllabus,
        MockExamUserReduxMetadata,
      })
    )
  );
}

export function useSubTopicStateSelector() {
  return useCustomSelector((state) => state.SubtopicUserReduxMetadata);
}

export function useTopicTestStateSelector() {
  return useCustomSelector((state) => state.TopicTestUserReduxMetadata);
}

export function useCustomTestStateSelector() {
  return useCustomSelector((state) => state.CustomTestUserReduxMetadata);
}

export function useSyllabusStateSelector() {
  return useCustomSelector((state) => state.subjectSyllabus);
}

export function useMockExamStateSelector() {
  return useCustomSelector((state) => state.MockExamUserReduxMetadata);
}
